import { type UseModalState, useModalState } from "@src/appV2/redesign/system/ui-components";
import { addMinutes, isBefore } from "date-fns";
import { useEffect, useRef } from "react";

const SHIFT_EXTENSION_GRACE_PERIOD_IN_MINUTES = 5;

interface UseShiftEndAndExtensionModalsParams {
  isShiftEndedWithoutExtension: boolean;
  shiftEndDate: Date;
  currentDate: Date;
}

interface UseShiftEndAndExtensionModalsResult {
  extendShiftModalState: UseModalState;
  shiftEndedModalState: UseModalState;
  isInGracePeriod: boolean;
  graceEndDate: Date;
}

/**
 * Coordinates the shift-ended and extend-shift modals around a 5-minute grace period.
 *
 * Rules:
 * 1. While the extend-shift form is open and the shift ends, suppress the
 *    shift-ended sheet for up to 5 minutes (grace period).
 * 2. If the worker closes the extend-shift form without creating an extension,
 *    show the shift-ended sheet immediately.
 * 3. If an extension is created (isShiftEndedWithoutExtension becomes false),
 *    the shift-ended sheet is never shown.
 * 4. If the grace period expires with the form still open, auto-close the form
 *    and show the shift-ended sheet.
 */
export function useShiftEndAndExtensionModals(
  params: UseShiftEndAndExtensionModalsParams
): UseShiftEndAndExtensionModalsResult {
  const { isShiftEndedWithoutExtension, shiftEndDate, currentDate } = params;

  const extendShiftModalState = useModalState();
  const shiftEndedModalState = useModalState();

  const graceEndDate = addMinutes(shiftEndDate, SHIFT_EXTENSION_GRACE_PERIOD_IN_MINUTES);
  const isWithinGracePeriod = isShiftEndedWithoutExtension && isBefore(currentDate, graceEndDate);
  const isExtendShiftFormOpen = extendShiftModalState.modalIsOpen;
  const isInGracePeriod = isExtendShiftFormOpen && isWithinGracePeriod;

  // Auto-close the extend shift form when the grace period expires
  useEffect(() => {
    if (isExtendShiftFormOpen && isShiftEndedWithoutExtension && !isWithinGracePeriod) {
      extendShiftModalState.closeModal();
    }
  }, [
    isExtendShiftFormOpen,
    isShiftEndedWithoutExtension,
    isWithinGracePeriod,
    extendShiftModalState,
  ]);

  // Show the shift-ended sheet when:
  // - The shift has ended without an extension
  // - It hasn't been shown yet in this session
  // - The grace period is not active (form open + within grace window)
  const hasShownShiftEndedSheet = useRef(false);
  useEffect(() => {
    if (
      isShiftEndedWithoutExtension &&
      !hasShownShiftEndedSheet.current &&
      !isInGracePeriod &&
      !isExtendShiftFormOpen
    ) {
      hasShownShiftEndedSheet.current = true;
      shiftEndedModalState.openModal();
    }
  }, [isShiftEndedWithoutExtension, shiftEndedModalState, isInGracePeriod, isExtendShiftFormOpen]);

  return {
    extendShiftModalState,
    shiftEndedModalState,
    isInGracePeriod,
    graceEndDate,
  };
}
